import React from 'react';
import { AlgoStrategy, OptionStrategy, OrderTransaction, OrderType, SecurityType, TradeDestination } from './lookups';
import { fieldNames, getInitialTradingFormValues, TradingFormValues } from './schema';
import { cloneDeep, get, merge } from 'lodash';
import { DeepPartial } from 'utility-types';
import { Leaves } from '../../types';
import DEFAULT_TRADING_FORM_TRANSLATIONS from './translations.json';
import { Currency } from '../../constants';

export type TradingFormOptions = {
    allowMutualFundTrading: boolean;
    allowOptionTrading: boolean;
    allowFractional: boolean;
    allowTrailingStop: boolean;
    marginPositions: boolean;
    cashPositions: boolean;
    allowBuying: boolean;
    allowStopOrder: boolean;
    allowStopLimitOrder: boolean;
    allowLimitOrder: boolean;
    allowNotionalOrder: boolean;
    allowSpreadTrading: boolean;
    allowBuyWrite: boolean;
    allowGoodTilCanceled: boolean;
    allowGoodTilDate: boolean;
    allowBondTrading: boolean;
    allowOco: boolean;
    allowOcoLimitBuying: boolean;
    allowBondQuotes: boolean;
    optionLevel: number;
    allowMoc: boolean;
    allowAlgoTrading: boolean;
    allowAllMarketTimeSessions: boolean;
    allowPreCoreMarketTimeSessions: boolean;
    allowPostCoreMarketTimeSessions: boolean;
    allowPrePostMarketTimeSessions: boolean;
    currency: string;
};

export const DEFAULT_TRADING_FORM_OPTIONS: TradingFormOptions = {
    allowMutualFundTrading: false,
    allowOptionTrading: false,
    allowFractional: false,
    allowTrailingStop: false,
    marginPositions: false,
    cashPositions: true,
    allowBuying: true,
    allowStopOrder: true,
    allowStopLimitOrder: true,
    allowLimitOrder: true,
    allowNotionalOrder: true,
    allowSpreadTrading: false,
    allowBuyWrite: false,
    allowGoodTilCanceled: true,
    allowGoodTilDate: true,
    allowBondTrading: false,
    allowOco: false,
    allowOcoLimitBuying: false,
    allowBondQuotes: false,
    allowMoc: false,
    optionLevel: 1,
    allowAlgoTrading: false,
    allowAllMarketTimeSessions: true,
    allowPreCoreMarketTimeSessions: true,
    allowPostCoreMarketTimeSessions: true,
    allowPrePostMarketTimeSessions: true,
    currency: Currency.UsDollar,
};

export const getTradingFormMeta = (values: TradingFormValues) => {
    const equityTrading = values.securityType === SecurityType.Equity;
    const spreadTrading =
        values.securityType === SecurityType.Option && values.optionStrategy !== OptionStrategy.SingleLeg;
    const optionTrading = values.securityType === SecurityType.Option;
    const singleLegOptionTrading = values.optionStrategy === OptionStrategy.SingleLeg;
    const mutualFundTrading = values.securityType === SecurityType.MutualFund;
    const accountTrading = values.tradeDestination === TradeDestination.SingleAccount;
    const inventoryTrading = values.tradeDestination === TradeDestination.AveragePriceAccount;
    const modelTrading = values.tradeDestination === TradeDestination.Model;
    const buying =
        values.transaction === OrderTransaction.Buy ||
        values.transaction === OrderTransaction.BuyToClose ||
        values.transaction === OrderTransaction.BuyToCover ||
        values.transaction === OrderTransaction.BuyToOpen;
    const selling =
        values.transaction === OrderTransaction.Sell ||
        values.transaction === OrderTransaction.SellToClose ||
        values.transaction === OrderTransaction.SellToOpen ||
        values.transaction === OrderTransaction.ShortSell;
    const shorting = values.transaction === OrderTransaction.ShortSell;
    const limitOrder = values.orderType === OrderType.Limit || values.orderType === OrderType.StopLimit;
    const stopOrder = values.orderType === OrderType.Stop || values.orderType === OrderType.StopLimit;
    const notionalOrder = values.orderType === OrderType.Notional;
    const trailingStopOrder = values.orderType === OrderType.TrailingStop;
    const marketOrder = values.orderType === OrderType.Market;
    const buyWrite = values.securityType === SecurityType.Option && values.optionStrategy === OptionStrategy.BuyWrite;
    const buyToCover = values.transaction === OrderTransaction.BuyToCover;
    const evenOrder = values.orderType === OrderType.Even;
    const bondTrading = values.securityType === SecurityType.Bond;
    const ocoOrder = values.ocoOrder;
    const marketOnCloseOrder = values.orderType === OrderType.MarketOnClose;
    // Should probably have security type and trading type sep
    const algoTrading = values.securityType === SecurityType.Algo;
    const algoTwap = values.algoStrategy === AlgoStrategy.TWAP;

    return {
        equityTrading,
        spreadTrading,
        optionTrading,
        singleLegOptionTrading,
        mutualFundTrading,
        accountTrading,
        inventoryTrading,
        modelTrading,
        buying,
        selling,
        shorting,
        limitOrder,
        stopOrder,
        notionalOrder,
        trailingStopOrder,
        marketOrder,
        buyWrite,
        algoTrading,
        buyToCover,
        evenOrder,
        bondTrading,
        ocoOrder,
        marketOnCloseOrder,
        algoTwap,
    };
};

export type TradingFormMeta = ReturnType<typeof getTradingFormMeta>;

export { DEFAULT_TRADING_FORM_TRANSLATIONS };

export type TradingFormTranslations = DeepPartial<typeof DEFAULT_TRADING_FORM_TRANSLATIONS>;

export type TradingFormTranslationKey = Leaves<typeof DEFAULT_TRADING_FORM_TRANSLATIONS>;

export const createTradingFormTranslateFunction = (translations?: TradingFormTranslations) => {
    const newTranslations = merge(cloneDeep(DEFAULT_TRADING_FORM_TRANSLATIONS), translations);
    return (key: TradingFormTranslationKey, values?: Record<string, unknown>) => {
        const value = get(newTranslations, key) as string | undefined;

        if (value === undefined) {
            throw new Error(`Translation not found for key \`${key}\``);
        }

        if (values) {
            return value.replace(/{{(.+)}}/g, (_match, token) => values[token] as string);
        }

        return value;
    };
};

export const DEFAULT_TRADING_FORM_CONTEXT_VALUE = {
    fieldNames,
    meta: getTradingFormMeta(getInitialTradingFormValues()),
    options: DEFAULT_TRADING_FORM_OPTIONS,
    translate: createTradingFormTranslateFunction(),
};

export const TradingFormContext = React.createContext(DEFAULT_TRADING_FORM_CONTEXT_VALUE);
